// Modified useServices.js

import axiosInstance, { ADMIN_BASEURL, isArsAuthRequiredCall } from './axiosInstance';

// How close to expiry we force a re-validate. 10% of the token's total TTL,
// capped at 10 minutes so a 7-day token doesn't nag the user too early.
// This is Greg's concern from the Jira: officers shouldn't open the app
// with <1h left on a 24h token, think they're good, and have it die mid-scene.
const ARS_EXPIRY_BUFFER_FRACTION = 0.10;
const ARS_EXPIRY_BUFFER_MAX_MS = 10 * 60 * 1000;

/**
 * Returns true if the ARS token is missing or within the re-validate buffer window.
 * Caller should redirect to passphrase re-validation when this returns true.
 */
const isArsTokenNearExpiry = () => {
    const arsToken = localStorage.getItem('ars_token');
    if (!arsToken) return true; // missing → force re-validate via MFA link
    const expiresAtStr = localStorage.getItem('ars_token_expires_at');
    if (!expiresAtStr) return true; // token present but no expiry recorded → stale
    const expiresAt = Number(expiresAtStr);
    const durationSec = Number(localStorage.getItem('ars_token_duration_sec')) || 86400;
    const now = Date.now();
    const remaining = expiresAt - now;
    const buffer = Math.min(durationSec * 1000 * ARS_EXPIRY_BUFFER_FRACTION, ARS_EXPIRY_BUFFER_MAX_MS);
    return remaining < buffer;
};

const useServices = () => {
    const GenerateHeaders = () => {
        // Prefer the ARS-scoped JWT if we have one (issued from /ars/.../validatePassphrase).
        // Fall back to the legacy dummy "accesstoken" so older code paths hitting
        // unauthenticated endpoints keep working unchanged.
        const arsToken = localStorage.getItem('ars_token');
        const token = arsToken || "accesstoken"
        const accountId = localStorage.getItem('userID')
        const uniqueCode = localStorage.getItem('token')

        var headers = {
            "headers": {
                "Authorization": `Bearer ${token}`
            }
        }

        // Add accountId and uniqueCode to headers if available
        if (accountId) {
            headers.headers["accountId"] = accountId
        }
        
        if (uniqueCode) {
            headers.headers["uniqueCode"] = uniqueCode
        }
        
        return headers
    }
    
    /**
     * Pre-call guard: if the ARS token is near expiry, redirect the user to
     * re-validate via passphrase before the call would fail mid-scene.
     * Skip the guard for the passphrase-validate endpoint itself (that's how
     * we GET the new token) and for account-link validation.
     */
    const enforceArsExpiryGuard = (endpoint) => {
        // Only guard ARS-scoped, auth-required calls. Admin/MFA/legacy and
        // auth-issuance endpoints are excluded by isArsAuthRequiredCall so a
        // missing token at bootstrap doesn't trip the redirect.
        if (!isArsAuthRequiredCall(endpoint)) return;
        if (!isArsTokenNearExpiry()) return;

        // Near expiry: clear stored token and force re-validate.
        try {
            localStorage.removeItem('ars_token');
            localStorage.removeItem('ars_token_expires_at');
            localStorage.removeItem('ars_token_duration_sec');
        } catch (e) { /* non-fatal */ }
        const userID = localStorage.getItem('userID');
        const uniqueCode = localStorage.getItem('token');
        if (userID && uniqueCode) {
            const currentPath = window.location.pathname + window.location.search;
            window.location.href = `/validateAcct/${userID}/${uniqueCode}?returnUrl=${encodeURIComponent(currentPath)}`;
        }
        throw new Error('ars_token_near_expiry');
    };

    // 401 kick-out is handled globally by the response interceptor in
    // axiosInstance.js, so per-method catch blocks here just propagate.

    const get = async (endpoint, cancelToken) => {
        enforceArsExpiryGuard(endpoint);
        const headers = GenerateHeaders();
        return axiosInstance.get(endpoint, {
            ...headers,
            cancelToken: cancelToken,
        });
    }

    const getValidateAccount = async (endpoint) => {
        const headers = GenerateHeaders();
        return axiosInstance({
            url: endpoint,
            baseURL: ADMIN_BASEURL,
            headers: headers.headers
        });
    }

    const post = async (endpoint, requestBody) => {
        enforceArsExpiryGuard(endpoint);
        const headers = GenerateHeaders();
        return axiosInstance.post(endpoint, requestBody, headers);
    }

    const put = async (endpoint, requestBody) => {
        enforceArsExpiryGuard(endpoint);
        const headers = GenerateHeaders();
        return axiosInstance.put(endpoint, requestBody, headers);
    }

    const deleteService = async (endpoint, requestBody) => {
        enforceArsExpiryGuard(endpoint);
        const headers = GenerateHeaders();
        return axiosInstance.delete(endpoint, headers);
    }

    return { get, post, put, deleteService, getValidateAccount }
}

export default useServices